import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/solid';
import Select from 'react-select';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { isSlamOrg } from '../../2_dashboard/config/patientSetupConfig';
import { canClinicianBeAssignedToPathway } from '../../../../utils/pathwaySpecialismUtils';
import { getFriendlyStageNames } from '../../../../utils/stageDisplayNames';
import { ORG_WORKFLOW_CONFIGS, OBS_OBSERVATION_STATES } from '../../../../constants/workflowConfig';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';

/** Portal props for react-select inside this AlertDialog (scroll-lock fix). */
const DIALOG_SELECT_PORTAL_PROPS = {
    classNamePrefix: 'td-select',
    menuPortalTarget: getScrollablePortalTarget(),
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

/**
 * AssignCliniciansDialog
 *
 * Purpose-built dialog for INITIAL clinician assignment on patients created without
 * clinicians (e.g. The Owl Centre workflow where clinicians are assigned retroactively).
 *
 * This is the counterpart to ReassignCliniciansDialog -- that component handles
 * swapping existing assignments, while this one handles the first-time assignment.
 *
 * Two modes, selected automatically based on the user's organisation:
 *
 * 1. DEFAULT mode (The Owl Centre, WLC, etc.):
 *    Deterministic stage mapping — Lead -> CI, SSI, DD, PAD; Supporting -> DD MDT.
 *    The user makes two selections (Lead + Supporting) and all five stage
 *    assignments are derived automatically. Calls assignCliniciansToPatient.
 *
 * 2. SLAM mode (3 Borough Adult ADHD and Autism Service, Urban ADHD):
 *    Per-stage assignment via reassignClinicians (rather than the bulk
 *    assignCliniciansToPatient). The exact shape depends on the PATIENT's
 *    stamped workflow, not just the org, because SLaM has two generations of
 *    patient side by side:
 *
 *      (a) Legacy two-clinician, SSI-first patients (stamped before Oct 2026,
 *          and every Urban ADHD patient). The user chooses one of two actions:
 *            - "Assign SSI Clinician" — assigns a clinician to SSI only
 *            - "Assign Lead Clinician" — assigns Lead + CI + DD + PAD
 *          The SSI and Lead assignments happen at different points in time.
 *
 *      (b) One-clinician, CI-first patients (the current SLaM preset). A single
 *          Lead runs the whole assessment in one appointment, so there is only
 *          one action: "Assign Lead Clinician", whose bundle is
 *          Lead + CI + SSI + DD + PAD. Including SSI in the bundle is what keeps
 *          every clinicianSSI-based permission check (SSI Review canEdit,
 *          isOriginalSSIClinician, RD_SSI, Documents attribution) working with
 *          no backend change. Because there is only one possible action, the
 *          chooser step is skipped and the dialog opens on the clinician picker.
 *
 *    Which generation a patient belongs to is read from `wf.isSSIFirst`
 *    (useWorkflowConfig on the patient model). When the caller only has a
 *    dashboard summary rather than the full patient document (PatientTable),
 *    it passes the summary's `workflowModuleOrder` instead — see the prop docs.
 *
 * Code patterns intentionally mirror ReassignCliniciansDialog for consistency:
 *   - Same Radix AlertDialog shell and layout
 *   - Same react-select dropdown styling (isMulti single-select pattern)
 *   - Same clinician loading query and isAssignableClinician filtering
 *   - Same submit/error/success flow
 *   - Same badge styling for clinician display
 *
 * Access control:
 *   - Only Org Admins and Support users can perform initial assignment.
 *   - Backend enforces this as defence in depth.
 *
 * @param {Object}  props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string}  props.patientId
 * @param {Object|null} props.patientDataModel  Full patient document (Patient
 *   Overview) or a minimal `{ clinicians, organisation, assessmentModule }`
 *   built from a dashboard summary (PatientTable). Existing assignments and
 *   the stamped `workflowConfig` (when present) are read from here.
 * @param {Function} props.refreshPatientData
 * @param {'ciFirst'|'ssiFirst'|null} [props.workflowModuleOrder]  Optional
 *   override for the SLaM generation check. Dashboard summaries carry the
 *   patient's module order as `workflowModuleOrder` (computed server-side from
 *   the stamped or legacy config) but not the stamp itself, so PatientTable
 *   passes this field through. When supplied it wins over the resolver; when
 *   omitted the dialog resolves the order from `patientDataModel`.
 */
export default function AssignCliniciansDialog({
  isOpen,
  onClose,
  patientId,
  patientDataModel,
  refreshPatientData,
  workflowModuleOrder = null,
}) {
  const { userData, optimisticUpdatePatient } = useContext(GlobalStateContext);
  const organisation = userData?.organisation;

  // Detect SLaM mode — drives the entire step machine and submission logic.
  // When true, the dialog assigns per stage via reassignClinicians; when
  // false, it uses the default Lead+Supporting flow via assignCliniciansToPatient.
  const isSlamMode = isSlamOrg(organisation);

  // Which SLaM generation is this patient? Resolve the patient's workflow the
  // same way every other screen does (stamped config first, legacy derivation
  // for unstamped patients, org preset when no model at all). The dashboard
  // only has a summary, so it may hand us the order directly instead.
  //
  //   isSlamOneClinician === true  → CI-first, one Lead runs everything
  //                                  (single Lead bundle including SSI)
  //   isSlamOneClinician === false → legacy SSI-first (SSI-vs-Lead chooser)
  //
  // Legacy patients are the safe default: anything that does not positively
  // resolve to CI-first keeps the two-clinician UX they were created under.
  const wf = useWorkflowConfig(patientDataModel, { organisation });
  const isSlamOneClinician = isSlamMode && (
    workflowModuleOrder
      ? workflowModuleOrder === 'ciFirst'
      : wf.isSSIFirst === false
  );

  // Read existing assignments from patientDataModel (if passed) so we can show
  // what's already assigned and tailor the available actions accordingly.
  const existingClinicians = patientDataModel?.clinicians || null;
  const hasExistingSSI = !!existingClinicians?.clinicianSSI?.clinicianId;
  const hasExistingLead = !!existingClinicians?.clinicianLead?.clinicianId;
  const existingSSIName = existingClinicians?.clinicianSSI?.clinicianName || null;

  // One-clinician patients have exactly one sensible first action when no Lead
  // exists yet, so we skip the chooser and open straight on the clinician
  // picker with the Lead action pre-selected. If a Lead already exists (the
  // dialog was opened to repair an empty SSI slot, or by mistake), fall back to
  // the chooser so the user sees what is left to do.
  const skipChooser = isSlamOneClinician && !hasExistingLead;
  const initialSlamStep = skipChooser ? 'selectClinician' : 'chooseAction';

  // Step machine varies by mode:
  //   Default: 'selectLead' | 'selectSupporting' | 'preview' | 'success'
  //   SLaM:    'chooseAction' | 'selectClinician' | 'preview' | 'success'
  const [step, setStep] = useState(isSlamMode ? initialSlamStep : 'selectLead');

  const [leadSelection, setLeadSelection] = useState(null);
  const [supportingSelection, setSupportingSelection] = useState(null);
  const [obsSelection, setObsSelection] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // SLaM-specific state: which assignment action the user chose
  //   'ssi'  = assign SSI clinician only
  //   'lead' = assign the Lead bundle. Legacy: Lead + CI + DD + PAD.
  //            One-clinician: Lead + CI + SSI + DD + PAD (see leadBundleStages).
  const [slamAction, setSlamAction] = useState(skipChooser ? 'lead' : null);
  // SLaM uses a single clinician selection (shared for both SSI-only and Lead-bundle)
  const [slamClinicianSelection, setSlamClinicianSelection] = useState(null);

  const [clinicianOptions, setClinicianOptions] = useState([]);

  // Determine patient pathway -- OBS stage is only relevant for autism patients
  const assessmentModule = patientDataModel?.assessmentModule;
  const isAutism = assessmentModule === 'adultAutism';

  // Whether this patient's workflow runs the Behavioural Observation stage.
  // Prefer the patient's stamped workflowConfig (set at creation); fall back to
  // the org's static config for patients stamped before obsFlow existed. Absent
  // → enabled, so TOC and every existing autism patient keep the OBS step.
  // `isAutismObs` gates the OBS-specific Step 2 behaviour: when Observation is
  // disabled (e.g. LMP), the dialog collects a plain MDT Review supporting
  // clinician instead — no OBS stage, no OBS→DD linkage — exactly like ADHD.
  const isObservationEnabled = useMemo(() => {
    const stamped = patientDataModel?.workflowConfig?.obsFlow?.observation;
    if (stamped !== undefined) return stamped !== OBS_OBSERVATION_STATES.DISABLED;
    return (
      ORG_WORKFLOW_CONFIGS?.[organisation]?.obsFlow?.observation
        !== OBS_OBSERVATION_STATES.DISABLED
    );
  }, [patientDataModel, organisation]);
  const isAutismObs = isAutism && isObservationEnabled;

  // Friendly stage names for display text — sourced from a shared utility
  // so ReassignCliniciansDialog and this dialog stay in sync.
  const friendlyStageName = useMemo(
    () => getFriendlyStageNames(assessmentModule),
    [assessmentModule]
  );

  // The stages covered by the SLaM "Lead" bundle, in workflow order. This is
  // the single place that decides whether SSI is part of the bundle, so the
  // backend operations, the optimistic patch, the preview list and the copy
  // all agree. Legacy SSI-first patients keep SSI out of the bundle because a
  // separate SSI clinician owns (or will own) that slot.
  const leadBundleStages = useMemo(
    () => (isSlamOneClinician
      ? ['Lead', 'CI', 'SSI', 'DD', 'PAD']
      : ['Lead', 'CI', 'DD', 'PAD']),
    [isSlamOneClinician]
  );

  // Human-readable list of the non-Lead stages in the bundle, e.g.
  // "Clinical Interview, Semi-Structured Interview, Diagnostic Decision, and
  // Post-Assessment Discussion". Built once so every sentence reads the same.
  const leadBundleStagesText = useMemo(() => {
    const names = leadBundleStages
      .filter((s) => s !== 'Lead')
      .map((s) => friendlyStageName[s]);
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }, [leadBundleStages, friendlyStageName]);

  // Reset state when the dialog opens. Deliberately keyed on isOpen (and the
  // org-level mode) only: initialSlamStep / skipChooser are read at open time
  // but must NOT be dependencies. They flip as soon as a successful assignment
  // refreshes patientDataModel (a Lead now exists), and re-running this reset
  // at that moment would throw the user off the success screen and back to
  // the first step.
  useEffect(() => {
    if (isOpen) {
      setStep(isSlamMode ? initialSlamStep : 'selectLead');
      setLeadSelection(null);
      setSupportingSelection(null);
      setObsSelection(null);
      setSlamAction(skipChooser ? 'lead' : null);
      setSlamClinicianSelection(null);
      setSubmitError('');
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSlamMode]);

  // Load clinicians in org -- mirrors the exact query from ReassignCliniciansDialog
  // to ensure the same options appear in both dialogs
  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      if (!isOpen || !organisation) return;
      try {
        const cliniciansQuery = query(
          collection(db, 'clinicians'),
          where('organisation', '==', organisation),
          where('userType', 'in', ['Org. Admin', 'Clinician'])
        );
        const snapshot = await getDocs(cliniciansQuery);
        if (cancelled) return;
        const opts = snapshot.docs
          .filter(docSnap => {
            const data = docSnap.data() || {};
            if (data.accountDisabled === true) return false;
            if (data.isAssignableClinician === false) return false;
            // Filter out clinicians whose pathway specialisms don't include the
            // patient's assessment module. This prevents mismatched assignments
            // at the UI level (backend enforces this as defence-in-depth too).
            if (!canClinicianBeAssignedToPathway(data, assessmentModule)) return false;
            return true;
          })
          .map(docSnap => {
            const data = docSnap.data() || {};
            const label = data.title && data.firstName && data.lastName
              ? `${data.title} ${data.firstName} ${data.lastName}`
              : (data.clinicianFullName || 'Unnamed Clinician');
            return { value: docSnap.id, label };
          });
        setClinicianOptions(opts);
      } catch (err) {
        console.error('Error loading clinician options:', err);
        if (!cancelled) setSubmitError('Failed to load clinicians. Please try again.');
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, [isOpen, organisation, assessmentModule]);

  // Supporting clinician options exclude the selected lead (default mode only)
  const supportingOptions = useMemo(() => {
    if (!leadSelection) return clinicianOptions;
    return clinicianOptions.filter(opt => opt.value !== leadSelection.value);
  }, [clinicianOptions, leadSelection]);

  // OBS clinician options exclude the selected lead (autism default mode only).
  // The OBS clinician CAN be the same as Lead (allowed edge case for small orgs),
  // but we default to excluding Lead for better UX since it's almost always
  // a different person. The backend accepts obsClinicianId == leadClinicianId.
  const obsOptions = useMemo(() => {
    if (!leadSelection) return clinicianOptions;
    return clinicianOptions.filter(opt => opt.value !== leadSelection.value);
  }, [clinicianOptions, leadSelection]);

  const canProceedToSupporting = !!leadSelection;
  const canProceedToPreview = !!leadSelection;

  // Handle the final submission for DEFAULT mode: call the backend with lead + supporting IDs.
  // After a successful backend call, an optimistic patch is applied to the dashboard's
  // patientsData so the clinician badges appear immediately on the PatientTable row
  // without waiting for patient_summary_trigger to propagate (~5-10s). The real
  // server-computed summary replaces this on the next SWR background refresh cycle.
  const handleConfirmDefault = useCallback(async () => {
    setSubmitError('');
    if (!leadSelection) return;
    setIsSubmitting(true);
    try {
      const assignFn = httpsCallable(functions, 'assignCliniciansToPatient');
      const payload = {
        patientId,
        leadClinicianId: leadSelection.value,
        supportingClinicianId: supportingSelection?.value || null,
      };
      // For autism patients that run Observation, include the OBS clinician
      // (backend silently ignores obsClinicianId for non-autism patients and,
      // now, for autism orgs with Observation disabled as defence-in-depth).
      if (isAutismObs && obsSelection) {
        payload.obsClinicianId = obsSelection.value;
      }
      const result = await assignFn(payload);
      if (result?.data?.success) {
        // Optimistic update: build a clinicians map matching the summary shape
        // so PatientTable renders badges immediately. The Lead clinician is
        // assigned to all primary stages; the supporting clinician (if any)
        // goes into DD's supporting arrays.
        try {
          const leadEntry = { clinicianId: leadSelection.value, clinicianName: leadSelection.label };

          // Build DD supporting arrays from both explicit supporting and OBS clinicians
          const ddSupportingIds = [];
          const ddSupportingNames = [];
          if (supportingSelection) {
            ddSupportingIds.push(supportingSelection.value);
            ddSupportingNames.push(supportingSelection.label);
          }
          // OBS clinician auto-adds as DD supporting (when different from Lead)
          if (isAutismObs && obsSelection && obsSelection.value !== leadSelection.value) {
            if (!ddSupportingIds.includes(obsSelection.value)) {
              ddSupportingIds.push(obsSelection.value);
              ddSupportingNames.push(obsSelection.label);
            }
          }

          const ddEntry = {
            ...leadEntry,
            supportingClinicianIds: ddSupportingIds,
            supportingClinicianNames: ddSupportingNames,
          };
          const cliniciansPatch = {
            clinicianLead: leadEntry,
            clinicianCI: leadEntry,
            clinicianSSI: leadEntry,
            clinicianDD: ddEntry,
            clinicianPAD: leadEntry,
          };
          // Add OBS slot for autism patients that run Observation
          if (isAutismObs && obsSelection) {
            cliniciansPatch.clinicianOBS = { clinicianId: obsSelection.value, clinicianName: obsSelection.label };
          }
          const allIds = [leadSelection.value];
          if (supportingSelection) allIds.push(supportingSelection.value);
          if (obsSelection) allIds.push(obsSelection.value);

          if (typeof optimisticUpdatePatient === 'function') {
            optimisticUpdatePatient(patientId, {
              clinicians: cliniciansPatch,
              assignedClinicianIds: [...new Set(allIds)].sort(),
            });
          }
        } catch (e) {
          console.warn('Optimistic clinician update failed (non-fatal):', e);
        }

        // Background refresh to eventually get the full server-computed summary
        if (typeof refreshPatientData === 'function') {
          try {
            await refreshPatientData(patientId, true);
          } catch (e) {
            console.warn('Refresh patient data failed (non-fatal):', e);
          }
        }
        setStep('success');
      } else {
        setSubmitError(result?.data?.message || 'Failed to assign clinicians.');
      }
    } catch (err) {
      console.error('Assign clinicians error:', err);
      const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
        ? 'Service not available yet. Backend endpoint is not deployed.'
        : (err?.message || 'An unexpected error occurred.');
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [leadSelection, supportingSelection, obsSelection, isAutism, patientId, refreshPatientData, optimisticUpdatePatient]);

  // Handle the final submission for SLAM mode: call reassignClinicians with per-stage
  // operations. SSI-only sends one operation; the Lead bundle sends one operation per
  // stage in leadBundleStages (four for legacy SSI-first patients, five for one-clinician
  // patients because SSI joins the bundle). This reuses the existing reassignment
  // infrastructure, which supports writing to individual clinician slots via Firestore
  // dot-notation updates (safe even when slots are null from initial patient setup).
  //
  // After a successful backend call, an optimistic patch is applied so the PatientTable
  // badges and "Assign Lead" button update immediately. The patch merges with any
  // existing clinician data (e.g. SSI-only assignment preserves null Lead slots, and
  // the legacy Lead bundle preserves the existing SSI slot).
  const handleConfirmSlam = useCallback(async () => {
    setSubmitError('');
    if (!slamClinicianSelection || !slamAction) return;
    setIsSubmitting(true);

    const toClinicianId = slamClinicianSelection.value;
    const toClinicianName = slamClinicianSelection.label;
    let operations;

    if (slamAction === 'ssi') {
      operations = [
        { stage: 'SSI', type: 'primary', toClinicianId },
      ];
    } else {
      operations = leadBundleStages.map((stage) => ({ stage, type: 'primary', toClinicianId }));
    }

    try {
      const reassignFn = httpsCallable(functions, 'reassignClinicians');
      const result = await reassignFn({ patientId, operations });
      if (result?.data?.success) {
        // Optimistic update: merge new assignments onto the existing clinicians map.
        // SSI-only patches just clinicianSSI; the Lead bundle patches every slot in
        // leadBundleStages. Existing slots not touched by this operation are preserved.
        try {
          const newEntry = { clinicianId: toClinicianId, clinicianName: toClinicianName };
          const prevClinicians = existingClinicians || {};

          let cliniciansPatch;
          if (slamAction === 'ssi') {
            cliniciansPatch = { ...prevClinicians, clinicianSSI: newEntry };
          } else {
            // Slot names follow the backend convention: stage 'CI' → clinicianCI, etc.
            // DD additionally carries (empty) supporting arrays to match the summary shape.
            cliniciansPatch = { ...prevClinicians };
            leadBundleStages.forEach((stage) => {
              cliniciansPatch[`clinician${stage}`] = stage === 'DD'
                ? { ...newEntry, supportingClinicianIds: [], supportingClinicianNames: [] }
                : newEntry;
            });
          }

          // Collect all non-null clinician IDs for the assignedClinicianIds array
          const allIds = new Set();
          Object.values(cliniciansPatch).forEach((slot) => {
            if (slot?.clinicianId) allIds.add(slot.clinicianId);
            (slot?.supportingClinicianIds || []).forEach((id) => { if (id) allIds.add(id); });
          });

          if (typeof optimisticUpdatePatient === 'function') {
            optimisticUpdatePatient(patientId, {
              clinicians: cliniciansPatch,
              assignedClinicianIds: [...allIds].sort(),
            });
          }
        } catch (e) {
          console.warn('Optimistic clinician update failed (non-fatal):', e);
        }

        // Background refresh to eventually get the full server-computed summary
        if (typeof refreshPatientData === 'function') {
          try {
            await refreshPatientData(patientId, true);
          } catch (e) {
            console.warn('Refresh patient data failed (non-fatal):', e);
          }
        }
        setStep('success');
      } else {
        setSubmitError(result?.data?.message || 'Failed to assign clinician.');
      }
    } catch (err) {
      console.error('SLaM assign clinician error:', err);
      const msg = err?.message?.includes('unavailable') || err?.code === 'functions/not-found'
        ? 'Service not available yet. Backend endpoint is not deployed.'
        : (err?.message || 'An unexpected error occurred.');
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [slamClinicianSelection, slamAction, leadBundleStages, patientId, refreshPatientData, optimisticUpdatePatient, existingClinicians]);

  // ── SLaM step renderers ──

  // Step 1 (SLaM): Choose whether to assign SSI clinician or Lead clinician.
  // Adapts based on what's already assigned — if SSI is present, defaults to
  // the Lead action and shows an info badge about the existing SSI assignment.
  //
  // One-clinician patients normally never see this step (skipChooser opens the
  // picker directly). They only land here when a Lead already exists, in which
  // case the Lead option is hidden anyway and the SSI option acts as a repair
  // path for an empty SSI slot, with copy that does not promise a separate
  // "first appointment".
  const renderChooseAction = () => (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Choose Assignment Type
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Select which clinician role you want to assign to this patient.
        </p>
      </div>

      {hasExistingSSI && (
        <div className="p-3 rounded-md border bg-blue-50 border-blue-200">
          <p className="text-xs text-blue-800">
            <span className="font-semibold">SSI Clinician already assigned:</span>{' '}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
              {existingSSIName}
            </span>
          </p>
        </div>
      )}

      <div className="space-y-2">
        {/* SSI-only option. Legacy: always offered while SSI is empty (the SSI
            appointment usually comes first). One-clinician: the Lead bundle
            already covers SSI, so only offer it once a Lead exists and the SSI
            slot is still empty. */}
        {!hasExistingSSI && (!isSlamOneClinician || hasExistingLead) && (
          <button
            type="button"
            onClick={() => { setSlamAction('ssi'); setSlamClinicianSelection(null); setSubmitError(''); setStep('selectClinician'); }}
            className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-violet-400 hover:bg-violet-50 transition-colors group"
          >
            <div className="font-semibold text-sm text-gray-900 group-hover:text-violet-800">
              Assign SSI Clinician
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {isSlamOneClinician
                ? `Assign a clinician to the ${friendlyStageName.SSI} only. In the one-clinician workflow this is normally the Lead clinician.`
                : 'Assign a clinician to the Semi-Structured Interview only. This is typically the first appointment.'}
            </p>
          </button>
        )}

        {!hasExistingLead && (
          <button
            type="button"
            onClick={() => { setSlamAction('lead'); setSlamClinicianSelection(null); setSubmitError(''); setStep('selectClinician'); }}
            className="w-full text-left p-3 rounded-md border border-gray-200 hover:border-violet-400 hover:bg-violet-50 transition-colors group"
          >
            <div className="font-semibold text-sm text-gray-900 group-hover:text-violet-800">
              Assign Lead Clinician
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Assign a Lead clinician to {leadBundleStagesText}.
              {hasExistingSSI && !isSlamOneClinician && ' The existing SSI assignment will remain unchanged.'}
              {hasExistingSSI && isSlamOneClinician && ' The existing SSI assignment will be replaced so one clinician runs the whole assessment.'}
            </p>
          </button>
        )}
      </div>

      {hasExistingSSI && hasExistingLead && (
        <div className="p-3 rounded-md border bg-amber-50 border-amber-200">
          <p className="text-xs text-amber-800">
            Both SSI and Lead clinicians are already assigned. Use the
            &quot;Reassign Clinicians&quot; option from the patient overview to make changes.
          </p>
        </div>
      )}

      {submitError && (
        <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
          {submitError}
        </div>
      )}

      <div className="mt-2 flex justify-end">
        <AlertDialog.Cancel asChild>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </AlertDialog.Cancel>
      </div>
    </div>
  );

  // Step 2 (SLaM): Select the clinician to assign.
  // The label and description adapt based on whether this is an SSI or Lead
  // assignment, and (for the Lead bundle) on which stages the bundle covers.
  // When the chooser was skipped there is nothing to go "back" to, so the
  // secondary action becomes Cancel instead.
  const renderSelectClinician = () => {
    const isSSI = slamAction === 'ssi';
    const leadDescription = isSlamOneClinician
      ? `Choose the Lead clinician. They will run the whole assessment in one appointment and be assigned to ${leadBundleStagesText}.`
      : `Choose the Lead clinician. They will be assigned to ${leadBundleStagesText}.`;
    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Select {isSSI ? 'SSI' : 'Lead'} Clinician
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {isSSI
              ? 'Choose the clinician who will conduct the Semi-Structured Interview.'
              : leadDescription
            }
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {isSSI ? 'SSI' : 'Lead'} Clinician <span className="text-red-500">*</span>
          </label>
          <Select
            {...DIALOG_SELECT_PORTAL_PROPS}
            isMulti
            options={clinicianOptions}
            value={slamClinicianSelection ? [slamClinicianSelection] : []}
            onChange={(selected) => {
              const newSelection = (selected && selected.length > 0) ? selected[selected.length - 1] : null;
              setSlamClinicianSelection(newSelection);
            }}
            placeholder={`Choose ${isSSI ? 'SSI' : 'Lead'} clinician`}
            isSearchable
            closeMenuOnSelect={true}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) e.preventDefault();
            }}
            className="text-left"
          />
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          {skipChooser ? (
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
          ) : (
            <button
              type="button"
              onClick={() => { setStep('chooseAction'); setSubmitError(''); }}
              className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              ← Back
            </button>
          )}
          <button
            type="button"
            onClick={() => { setSubmitError(''); setStep('preview'); }}
            disabled={!slamClinicianSelection}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              !slamClinicianSelection ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            Review →
          </button>
        </div>
      </div>
    );
  };

  // Preview step for SLaM mode — shows the exact stage assignments that will be applied
  const renderSlamPreview = () => {
    const isSSI = slamAction === 'ssi';
    const clinicianName = slamClinicianSelection?.label;

    // One row per stage in the bundle so the preview is a literal list of
    // what reassignClinicians will write (SSI appears only for one-clinician patients).
    const stageAssignments = isSSI
      ? [{ stage: friendlyStageName.SSI, clinician: clinicianName }]
      : leadBundleStages.map((stage) => ({
          stage: stage === 'Lead' ? 'Lead Clinician' : friendlyStageName[stage],
          clinician: clinicianName,
        }));

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Review Assignment
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Please review the clinician assignment before confirming.
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-violet-100 text-violet-800 border border-violet-300 shadow-sm">
              {clinicianName}
            </span>
            <span className="text-sm text-gray-700 font-medium">
              will be assigned as {isSSI ? 'SSI Clinician' : 'Lead Clinician'}
            </span>
          </div>
        </div>

        <div className="p-3 rounded-md border bg-blue-50 border-blue-200">
          <h4 className="text-xs font-semibold mb-2 text-blue-900">Stage Assignments</h4>
          <ul className="text-xs space-y-1.5 text-blue-800">
            {stageAssignments.map(({ stage, clinician }) => (
              <li key={stage} className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  <span className="font-semibold">{stage}</span>
                  {' — '}
                  {clinician}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* If this is a Lead assignment and SSI is already assigned to someone else,
            say exactly what happens to it: legacy bundles leave SSI alone; the
            one-clinician bundle includes SSI and therefore replaces it. */}
        {!isSSI && hasExistingSSI && (
          <div className="p-2 rounded-md border bg-gray-50 border-gray-200">
            <p className="text-xs text-gray-600">
              <span className="font-medium">Note:</span>{' '}
              {isSlamOneClinician
                ? `The existing SSI assignment (${existingSSIName}) will be replaced by ${clinicianName}.`
                : `The existing SSI assignment (${existingSSIName}) will remain unchanged.`}
            </p>
          </div>
        )}

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('selectClinician'); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmSlam}
            disabled={isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Assigning...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Success screen for SLaM mode
  const renderSlamSuccess = () => {
    const isSSI = slamAction === 'ssi';
    const clinicianName = slamClinicianSelection?.label;

    // Legacy bundles report the untouched SSI slot; the one-clinician bundle
    // already lists SSI in leadBundleStagesText, so no extra line is needed.
    const bullets = isSSI
      ? [`${clinicianName} is now assigned to the ${friendlyStageName.SSI}.`]
      : [
          `${clinicianName} is now the Lead Clinician, assigned to ${leadBundleStagesText}.`,
          ...(hasExistingSSI && !isSlamOneClinician
            ? [`SSI remains assigned to ${existingSSIName}.`]
            : []),
        ];

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">Clinician Assigned Successfully</h3>
        </div>
        <div className="border rounded-md p-3 bg-green-50 border-green-200">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CheckCircleSolid className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-xs font-semibold mb-2 text-green-800">Assignment applied</h4>
              <ul className="text-xs space-y-1 text-green-700 list-disc pl-4">
                {bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onClose()}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors bg-violet-600 hover:bg-violet-700"
          >
            Finish
          </button>
        </div>
      </div>
    );
  };

  // ── Default mode step renderers ──

  const renderSelectLead = () => (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Step 1: Select Lead Clinician
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          The lead clinician will be assigned to all assessment stages.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Lead Clinician <span className="text-red-500">*</span>
        </label>
        <Select
          {...DIALOG_SELECT_PORTAL_PROPS}
          isMulti
          options={clinicianOptions}
          value={leadSelection ? [leadSelection] : []}
          onChange={(selected) => {
            const newSelection = (selected && selected.length > 0) ? selected[selected.length - 1] : null;
            setLeadSelection(newSelection);
            if (newSelection && supportingSelection?.value === newSelection.value) {
              setSupportingSelection(null);
            }
          }}
          placeholder="Choose lead clinician"
          isSearchable
          closeMenuOnSelect={true}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.shiftKey) e.preventDefault();
          }}
          className="text-left"
        />
      </div>

      {submitError && (
        <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
          {submitError}
        </div>
      )}

      <div className="mt-2 flex justify-between">
        <AlertDialog.Cancel asChild>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </AlertDialog.Cancel>
        <button
          type="button"
          onClick={() => { setSubmitError(''); setStep('selectSupporting'); }}
          disabled={!canProceedToSupporting}
          className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
            !canProceedToSupporting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  );

  // Step 2 for default mode adapts based on the patient's pathway:
  // - ADHD patients: "MDT Review Clinician" (DD supporting only)
  // - Autism patients: "Behavioural Observation Clinician" (OBS stage + auto DD supporting)
  const renderSelectSupporting = () => (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Step 2: Select {isAutismObs ? 'Behavioural Observation Clinician' : 'MDT Review Clinician'}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {isAutismObs
            ? `The observation clinician will conduct the ${friendlyStageName.OBS} and participate in the ${friendlyStageName.DD} alongside the lead clinician. You can skip this step and add one later.`
            : `The MDT review clinician will participate in the ${friendlyStageName.DD} alongside the lead clinician. You can skip this step and add one later.`
          }
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {isAutismObs ? 'Behavioural Observation Clinician' : 'MDT Review Clinician'} (optional)
        </label>
        <Select
          {...DIALOG_SELECT_PORTAL_PROPS}
          isMulti
          options={isAutismObs ? obsOptions : supportingOptions}
          value={isAutismObs
            ? (obsSelection ? [obsSelection] : [])
            : (supportingSelection ? [supportingSelection] : [])
          }
          onChange={(selected) => {
            const newSelection = (selected && selected.length > 0) ? selected[selected.length - 1] : null;
            if (isAutismObs) {
              setObsSelection(newSelection);
            } else {
              setSupportingSelection(newSelection);
            }
          }}
          placeholder={`Choose ${isAutismObs ? 'observation' : 'MDT review'} clinician (optional)`}
          isSearchable
          closeMenuOnSelect={true}
          isClearable
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.shiftKey) e.preventDefault();
          }}
          className="text-left"
        />
      </div>

      {submitError && (
        <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
          {submitError}
        </div>
      )}

      <div className="mt-2 flex justify-between">
        <button
          type="button"
          onClick={() => { setStep('selectLead'); setSubmitError(''); }}
          className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => { setSubmitError(''); setStep('preview'); }}
          disabled={!canProceedToPreview}
          className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
            !canProceedToPreview ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          Review →
        </button>
      </div>
    </div>
  );

  const renderPreview = () => {
    // Build the supporting clinician name for the DD preview row.
    // For autism patients that run Observation, the OBS clinician auto-becomes
    // DD supporting; otherwise (ADHD, or autism with Observation disabled) it is
    // the plain MDT Review supporting clinician.
    const ddSupportingName = isAutismObs
      ? obsSelection?.label
      : supportingSelection?.label;

    // For Autism, the DD stage label shows as "MDT Review" (the canonical Autism name);
    // for ADHD, we show the compound "Diagnostic Decision (MDT Review)" to clarify
    // the relationship between the stage name and the MDT process.
    const ddPreviewLabel = isAutism
      ? friendlyStageName.DD
      : `${friendlyStageName.DD} (MDT Review)`;

    const stageAssignments = [
      { stage: friendlyStageName.CI, clinician: leadSelection?.label, role: 'Lead' },
      { stage: friendlyStageName.SSI, clinician: leadSelection?.label, role: 'Lead' },
      { stage: ddPreviewLabel, clinician: leadSelection?.label, role: 'Lead', supporting: ddSupportingName },
      { stage: friendlyStageName.PAD, clinician: leadSelection?.label, role: 'Lead' },
    ];
    // Insert OBS stage for autism patients that run Observation
    if (isAutismObs && obsSelection) {
      stageAssignments.splice(3, 0, {
        stage: friendlyStageName.OBS,
        clinician: obsSelection.label,
        role: 'OBS',
      });
    }

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Review Assignments
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Please review the clinician assignments before confirming.
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-violet-100 text-violet-800 border border-violet-300 shadow-sm">
              {leadSelection?.label}
            </span>
            <span className="text-sm text-gray-700 font-medium">will be the Lead Clinician</span>
          </div>
        </div>

        {isAutismObs && obsSelection && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {obsSelection.label}
              </span>
              <span className="text-sm text-gray-700 font-medium">will be the {friendlyStageName.OBS} Clinician</span>
            </div>
          </div>
        )}

        {!isAutismObs && supportingSelection && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {supportingSelection.label}
              </span>
              <span className="text-sm text-gray-700 font-medium">will be the MDT Review Clinician</span>
            </div>
          </div>
        )}

        <div className="p-3 rounded-md border bg-blue-50 border-blue-200">
          <h4 className="text-xs font-semibold mb-2 text-blue-900">Stage Assignments</h4>
          <ul className="text-xs space-y-1.5 text-blue-800">
            {stageAssignments.map(({ stage, clinician, supporting }) => (
              <li key={stage} className="flex items-start space-x-1">
                <span className="text-xs mt-0.5">•</span>
                <span>
                  <span className="font-semibold">{stage}</span>
                  {' - '}
                  {clinician}
                  {supporting && (
                    <span className="text-blue-600"> + {supporting} (MDT review)</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {submitError && (
          <div className="p-2 mt-2 text-xs font-semibold text-red-600 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <button
            type="button"
            onClick={() => { setStep('selectSupporting'); setSubmitError(''); }}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            disabled={isSubmitting}
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirmDefault}
            disabled={isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Assigning...</span>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Success screen for default mode -- mirrors ReassignCliniciansDialog success pattern
  const renderSuccess = () => {
    const bullets = [
      `${leadSelection?.label} is now the Lead Clinician, assigned to ${friendlyStageName.CI}, ${friendlyStageName.SSI}, ${friendlyStageName.DD}, and ${friendlyStageName.PAD}.`,
    ];
    if (isAutismObs && obsSelection) {
      bullets.push(
        `${obsSelection.label} is the ${friendlyStageName.OBS} clinician and will also participate in the ${friendlyStageName.DD}.`
      );
    }
    if (!isAutismObs && supportingSelection) {
      bullets.push(
        `${supportingSelection.label} is the MDT review clinician for the ${friendlyStageName.DD}.`
      );
    }

    return (
      <div className="space-y-4">
        <div className="border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-900">Clinicians Assigned Successfully</h3>
        </div>
        <div className="border rounded-md p-3 bg-green-50 border-green-200">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CheckCircleSolid className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-xs font-semibold mb-2 text-green-800">Assignments applied</h4>
              <ul className="text-xs space-y-1 text-green-700 list-disc pl-4">
                {bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onClose()}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors bg-violet-600 hover:bg-violet-700"
          >
            Finish
          </button>
        </div>
      </div>
    );
  };

  // ── Render the step machine ──

  const renderCurrentStep = () => {
    if (isSlamMode) {
      switch (step) {
        case 'chooseAction':    return renderChooseAction();
        case 'selectClinician': return renderSelectClinician();
        case 'preview':         return renderSlamPreview();
        case 'success':         return renderSlamSuccess();
        default:                return renderChooseAction();
      }
    }
    switch (step) {
      case 'selectLead':       return renderSelectLead();
      case 'selectSupporting': return renderSelectSupporting();
      case 'preview':          return renderPreview();
      case 'success':          return renderSuccess();
      default:                 return renderSelectLead();
    }
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <AlertDialog.Title className="text-xl font-semibold mb-4">
            Assign Clinicians
          </AlertDialog.Title>
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              {isSlamOneClinician
                ? 'Assign the Lead clinician who will run the whole assessment for this patient.'
                : isSlamMode
                  ? 'Assign a clinician to a specific assessment stage for this patient.'
                  : 'Assign a lead and MDT review clinician to this patient.'}
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <div className="text-left">
            {renderCurrentStep()}
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
